import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, ExternalLink, Map as MapIcon } from "lucide-react";
import PageHead from "@/app/PageHead";
import RecipeCard from "@/features/recipe/RecipeCard";
import { getCatalog } from "@/lib/catalog-client";
import { computeRecipeCounts, recipesForNode } from "@/lib/bpmn-overlays";
import { iconForDomain } from "@/lib/domain-icons";
import type { Catalog, TaxonomyDoc, TaxonomyNode } from "@/lib/types";

let taxonomyPromise: Promise<TaxonomyDoc> | null = null;
function loadTaxonomy(): Promise<TaxonomyDoc> {
  if (!taxonomyPromise) {
    taxonomyPromise = fetch("/data/taxonomy.json").then((r) => {
      if (!r.ok) throw new Error(`taxonomy.json: ${r.status}`);
      return r.json();
    });
  }
  return taxonomyPromise;
}

/** Domain accents — every end-to-end gets its own color band. */
const ACCENT: Record<string, { tint: string; ring: string; chip: string; edge: string }> = {
  "acquire-to-dispose": { tint: "from-violet-100 to-violet-50 dark:from-violet-950/50 dark:to-slate-900", ring: "ring-violet-300 dark:ring-violet-900", chip: "bg-violet-200 text-violet-900 dark:bg-violet-900/60 dark:text-violet-100", edge: "border-l-violet-400 dark:border-l-violet-700" },
  "administer-to-operate": { tint: "from-slate-100 to-slate-50 dark:from-slate-800/50 dark:to-slate-900", ring: "ring-slate-300 dark:ring-slate-700", chip: "bg-slate-200 text-slate-900 dark:bg-slate-800 dark:text-slate-100", edge: "border-l-slate-400 dark:border-l-slate-600" },
  "case-to-resolution": { tint: "from-rose-100 to-rose-50 dark:from-rose-950/50 dark:to-slate-900", ring: "ring-rose-300 dark:ring-rose-900", chip: "bg-rose-200 text-rose-900 dark:bg-rose-900/60 dark:text-rose-100", edge: "border-l-rose-400 dark:border-l-rose-700" },
  "concept-to-market": { tint: "from-fuchsia-100 to-fuchsia-50 dark:from-fuchsia-950/50 dark:to-slate-900", ring: "ring-fuchsia-300 dark:ring-fuchsia-900", chip: "bg-fuchsia-200 text-fuchsia-900 dark:bg-fuchsia-900/60 dark:text-fuchsia-100", edge: "border-l-fuchsia-400 dark:border-l-fuchsia-700" },
  "design-to-retire": { tint: "from-indigo-100 to-indigo-50 dark:from-indigo-950/50 dark:to-slate-900", ring: "ring-indigo-300 dark:ring-indigo-900", chip: "bg-indigo-200 text-indigo-900 dark:bg-indigo-900/60 dark:text-indigo-100", edge: "border-l-indigo-400 dark:border-l-indigo-700" },
  "forecast-to-plan": { tint: "from-sky-100 to-sky-50 dark:from-sky-950/50 dark:to-slate-900", ring: "ring-sky-300 dark:ring-sky-900", chip: "bg-sky-200 text-sky-900 dark:bg-sky-900/60 dark:text-sky-100", edge: "border-l-sky-400 dark:border-l-sky-700" },
  "hire-to-retire": { tint: "from-amber-100 to-amber-50 dark:from-amber-950/50 dark:to-slate-900", ring: "ring-amber-300 dark:ring-amber-900", chip: "bg-amber-200 text-amber-900 dark:bg-amber-900/60 dark:text-amber-100", edge: "border-l-amber-400 dark:border-l-amber-700" },
  "inventory-to-deliver": { tint: "from-teal-100 to-teal-50 dark:from-teal-950/50 dark:to-slate-900", ring: "ring-teal-300 dark:ring-teal-900", chip: "bg-teal-200 text-teal-900 dark:bg-teal-900/60 dark:text-teal-100", edge: "border-l-teal-400 dark:border-l-teal-700" },
  "order-to-cash": { tint: "from-emerald-100 to-emerald-50 dark:from-emerald-950/50 dark:to-slate-900", ring: "ring-emerald-300 dark:ring-emerald-900", chip: "bg-emerald-200 text-emerald-900 dark:bg-emerald-900/60 dark:text-emerald-100", edge: "border-l-emerald-400 dark:border-l-emerald-700" },
  "plan-to-produce": { tint: "from-orange-100 to-orange-50 dark:from-orange-950/50 dark:to-slate-900", ring: "ring-orange-300 dark:ring-orange-900", chip: "bg-orange-200 text-orange-900 dark:bg-orange-900/60 dark:text-orange-100", edge: "border-l-orange-400 dark:border-l-orange-700" },
  "project-to-profit": { tint: "from-lime-100 to-lime-50 dark:from-lime-950/50 dark:to-slate-900", ring: "ring-lime-300 dark:ring-lime-900", chip: "bg-lime-200 text-lime-900 dark:bg-lime-900/60 dark:text-lime-100", edge: "border-l-lime-400 dark:border-l-lime-700" },
  "prospect-to-quote": { tint: "from-cyan-100 to-cyan-50 dark:from-cyan-950/50 dark:to-slate-900", ring: "ring-cyan-300 dark:ring-cyan-900", chip: "bg-cyan-200 text-cyan-900 dark:bg-cyan-900/60 dark:text-cyan-100", edge: "border-l-cyan-400 dark:border-l-cyan-700" },
  "record-to-report": { tint: "from-blue-100 to-blue-50 dark:from-blue-950/50 dark:to-slate-900", ring: "ring-blue-300 dark:ring-blue-900", chip: "bg-blue-200 text-blue-900 dark:bg-blue-900/60 dark:text-blue-100", edge: "border-l-blue-400 dark:border-l-blue-700" },
  "service-to-deliver": { tint: "from-pink-100 to-pink-50 dark:from-pink-950/50 dark:to-slate-900", ring: "ring-pink-300 dark:ring-pink-900", chip: "bg-pink-200 text-pink-900 dark:bg-pink-900/60 dark:text-pink-100", edge: "border-l-pink-400 dark:border-l-pink-700" },
  "source-to-pay": { tint: "from-purple-100 to-purple-50 dark:from-purple-950/50 dark:to-slate-900", ring: "ring-purple-300 dark:ring-purple-900", chip: "bg-purple-200 text-purple-900 dark:bg-purple-900/60 dark:text-purple-100", edge: "border-l-purple-400 dark:border-l-purple-700" },
  "work-management": { tint: "from-stone-100 to-stone-50 dark:from-stone-800/50 dark:to-slate-900", ring: "ring-stone-300 dark:ring-stone-700", chip: "bg-stone-200 text-stone-900 dark:bg-stone-800 dark:text-stone-100", edge: "border-l-stone-400 dark:border-l-stone-600" },
};
const DEFAULT_ACCENT = { tint: "from-slate-100 to-slate-50 dark:from-slate-800/50 dark:to-slate-900", ring: "ring-slate-300 dark:ring-slate-700", chip: "bg-slate-200 text-slate-900 dark:bg-slate-800 dark:text-slate-100", edge: "border-l-slate-400 dark:border-l-slate-600" };

function accentFor(nodeId: string) {
  const root = nodeId.split("/")[0] ?? nodeId;
  return ACCENT[root] ?? DEFAULT_ACCENT;
}

interface Tree {
  /** Recipe count rolled up for each id (includes descendants). */
  counts: Map<string, number>;
  /** Direct children of a given parent (id == undefined for top level). */
  childrenOf: (parentId: string | undefined) => TaxonomyNode[];
  byId: Map<string, TaxonomyNode>;
  trail: (nodeId: string) => TaxonomyNode[];
}

function buildTree(catalog: Catalog, taxonomy: TaxonomyDoc): Tree {
  const byId = new Map<string, TaxonomyNode>();
  for (const n of taxonomy.nodes) byId.set(n.id, n);
  const counts = computeRecipeCounts(catalog, taxonomy);
  const kidsByParent = new Map<string | undefined, TaxonomyNode[]>();
  for (const n of taxonomy.nodes) {
    const k = n.parent;
    const arr = kidsByParent.get(k) ?? [];
    arr.push(n);
    kidsByParent.set(k, arr);
  }
  for (const arr of kidsByParent.values()) arr.sort((a, b) => a.title.localeCompare(b.title));
  return {
    counts,
    byId,
    childrenOf: (p) => kidsByParent.get(p) ?? [],
    trail: (id) => {
      const out: TaxonomyNode[] = [];
      let cur: TaxonomyNode | undefined = byId.get(id);
      while (cur) {
        out.unshift(cur);
        cur = cur.parent ? byId.get(cur.parent) : undefined;
      }
      return out;
    },
  };
}

interface NodeCardProps {
  node: TaxonomyNode;
  count: number;
  rootSlug: string;
}

function NodeCard({ node, count, rootSlug }: NodeCardProps) {
  const a = accentFor(rootSlug);
  const description = node.description?.replace(/\s+/g, " ").trim() ?? "";
  const Icon = iconForDomain(rootSlug);
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
    >
      <Link
        to={`/processes/${node.id}`}
        className={[
          "group block rounded-xl border border-slate-200 dark:border-slate-800",
          "border-l-4",
          a.edge,
          "bg-gradient-to-br p-5",
          "shadow-sm hover:shadow-md ring-1 ring-transparent hover:ring-2 transition-all hover:-translate-y-0.5",
          "motion-reduce:transform-none motion-reduce:transition-none",
          a.tint,
          `hover:${a.ring}`,
        ].join(" ")}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1">
            <span
              className={`inline-flex items-center justify-center w-10 h-10 rounded-lg ${a.chip} shrink-0`}
              aria-hidden
            >
              <Icon size={20} strokeWidth={2} />
            </span>
            <h3 className="text-base sm:text-lg font-semibold leading-snug pt-1">{node.title}</h3>
          </div>
          <span
            className={`text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${a.chip}`}
            aria-label={`${count} recipe${count === 1 ? "" : "s"}`}
          >
            {count} {count === 1 ? "recipe" : "recipes"}
          </span>
        </div>
        {description ? (
          <p className="mt-3 text-sm text-slate-600 dark:text-slate-300 line-clamp-3">{description}</p>
        ) : null}
        <p className="mt-3 inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 group-hover:text-brand-700 dark:group-hover:text-brand-400 transition-colors">
          {node.level === "end-to-end"
            ? "End-to-end process"
            : node.level === "area"
              ? "Process area"
              : "Process"}
          <ChevronRight size={12} className="transition-transform group-hover:translate-x-0.5 motion-reduce:transform-none" />
        </p>
      </Link>
    </motion.div>
  );
}

interface ProcessBrowserProps {
  current: TaxonomyNode | null;
}

export function ProcessBrowser({ current }: ProcessBrowserProps) {
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [taxonomy, setTaxonomy] = useState<TaxonomyDoc | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getCatalog(), loadTaxonomy()])
      .then(([c, t]) => {
        if (cancelled) return;
        setCatalog(c);
        setTaxonomy(t);
      })
      .catch(() => {
        if (!cancelled) {
          // In tests / offline / pre-build the catalog isn't available; render empty state.
          setCatalog({ generated_at: "", recipes: [] });
          setTaxonomy({ version: "0", nodes: [], bpmn_files: [] });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const tree = useMemo<Tree | null>(() => {
    if (!catalog || !taxonomy) return null;
    return buildTree(catalog, taxonomy);
  }, [catalog, taxonomy]);

  if (!tree || !catalog) {
    return (
      <p className="text-slate-500" aria-live="polite">
        Loading the catalog…
      </p>
    );
  }

  const children = tree.childrenOf(current?.id);
  const visibleChildren = children
    .filter((c) => (tree.counts.get(c.id) ?? 0) > 0 || !current)
    .sort((a, b) => (tree.counts.get(b.id) ?? 0) - (tree.counts.get(a.id) ?? 0));

  const recipesHere = current ? recipesForNode(catalog, current.id) : [];
  const trail = current ? tree.trail(current.id) : [];
  const rootSlug = current ? current.id.split("/")[0]! : "";

  return (
    <section>
      <PageHead
        title={current ? current.title : "Cowork Cookbook"}
        description={
          current?.description ??
          "A community catalog of Microsoft Copilot Cowork recipes for Dynamics 365 Finance & Supply Chain Management, organized along the Microsoft Business Process Catalog."
        }
      />
      {current ? (
        <nav aria-label="Breadcrumb" className="text-sm text-slate-500 mb-2 flex flex-wrap gap-1">
          <Link to="/" className="hover:underline">
            All processes
          </Link>
          {trail.map((n, i) => (
            <span key={n.id} className="flex items-center gap-1">
              <span aria-hidden>/</span>
              {i === trail.length - 1 ? (
                <span className="text-slate-700 dark:text-slate-300">{n.title}</span>
              ) : (
                <Link to={`/processes/${n.id}`} className="hover:underline">
                  {n.title}
                </Link>
              )}
            </span>
          ))}
        </nav>
      ) : null}

      <header className="mb-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
            {current ? current.title : "Browse the cookbook"}
          </h1>
          {current ? (
            <Link
              to={`/map/${current.id}`}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800"
              title={`Open ${current.title} on the process map`}
            >
              <MapIcon size={14} aria-hidden /> View on map
            </Link>
          ) : null}
        </div>
        {current?.description ? (
          <p className="mt-2 max-w-3xl text-slate-700 dark:text-slate-300">{current.description}</p>
        ) : (
          <p className="mt-2 max-w-3xl text-slate-700 dark:text-slate-300">
            Pick a business process to see recipes for Microsoft Copilot Cowork.
          </p>
        )}
        {current?.learn_url ? (
          <p className="mt-2 text-sm">
            <a
              href={current.learn_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-brand-600 dark:text-brand-500 hover:underline"
            >
              Microsoft Learn reference <ExternalLink size={12} aria-hidden />
            </a>
          </p>
        ) : null}
      </header>

      {visibleChildren.length > 0 ? (
        <>
          <h2 className="text-lg font-semibold mb-3">
            {current
              ? current.level === "end-to-end"
                ? "Process areas"
                : current.level === "area"
                  ? "Processes"
                  : "Sub-processes"
              : `End-to-end processes (${visibleChildren.length})`}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {visibleChildren.map((n) => (
              <NodeCard
                key={n.id}
                node={n}
                count={tree.counts.get(n.id) ?? 0}
                rootSlug={rootSlug || n.id}
              />
            ))}
          </div>
        </>
      ) : null}

      {recipesHere.length > 0 ? (
        <>
          <h2 className="mt-8 text-lg font-semibold mb-3">
            Recipes tagged here ({recipesHere.length})
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {recipesHere.map((r) => (
              <RecipeCard key={r.id} recipe={r} />
            ))}
          </div>
        </>
      ) : null}

      {visibleChildren.length === 0 && recipesHere.length === 0 ? (
        <p className="text-slate-600 dark:text-slate-300">
          No recipes here yet. {" "}
          <Link to="/recipes" className="text-brand-600 hover:underline dark:text-brand-500">
            See all recipes
          </Link>
          .
        </p>
      ) : null}
    </section>
  );
}

export default function ProcessBrowserPage() {
  // Splat-style: useParams returns { "*": "<rest-of-path>" } via the wildcard route.
  const params = useParams<{ "*": string }>();
  const slug = (params["*"] ?? "").trim();
  const [taxonomy, setTaxonomy] = useState<TaxonomyDoc | null>(null);
  useEffect(() => {
    loadTaxonomy().then(setTaxonomy);
  }, []);
  if (slug && !taxonomy) {
    return (
      <p className="text-slate-500" aria-live="polite">
        Loading…
      </p>
    );
  }
  const node = slug && taxonomy ? taxonomy.nodes.find((n) => n.id === slug) ?? null : null;
  if (slug && !node) {
    return (
      <section>
        <h1 className="text-2xl font-semibold">Unknown process</h1>
        <p className="mt-2 text-slate-700 dark:text-slate-300">
          No process named <code>{slug}</code>.{" "}
          <Link to="/" className="text-brand-600 hover:underline dark:text-brand-500">
            Back to all processes
          </Link>
          .
        </p>
      </section>
    );
  }
  return <ProcessBrowser current={node} />;
}
